import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  Search,
  RefreshCw,
  X,
  Eye,
  CalendarDays,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  PackageCheck,
  PlusCircle,
  MapPin,
} from "lucide-react";
import {
  useLazyGetMyBookingsQuery,
  useLazyGetBookingDetailsByIdQuery,
  useCancelBookingsMutation,
  useRescheduleBookingMutation,
  useRescheduleSlotsMutation,
  useLazyGetMyPackagesQuery,
  useFreePackageBookingMutation,
  useFreePackageRescheduleSlotsMutation,
} from "../../../redux/actions/Customer/serviceCustomer";
import {
  IMyBookingsResponse,
  BookingSlot,
} from "../../../types/Response/CustomerResponse";
import { useAlert } from "../../../components/customer/AlertContext";
import {
  IGetMyBookingsRequest,
  ICreateFreeBookingsRequest,
} from "../../../types/Request/CustomerRequest";

export interface IMultiPackageUsedresponse {
  orderServiceItemId: number;
  serviceId: number;
  serviceName: string;
  servicePackageId: number;
  packageName: string;
  petProfileId: number;
  petName: string;
  includedBookings: number;
  usedBookings: number;
  remainingBookings: number;
  validForMonths: number;
  purchasedOn: string;
  expiresOn: string;
  customerPackageId: number;
}

const formatLocalDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthRangeISO = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    fromDate: formatLocalDateISO(start),
    toDate: formatLocalDateISO(end),
  };
};

const formatTimeHHMM = (timeStr?: string): string => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return timeStr;
};

const formatDurationToHr = (totalMinutes?: number): string => {
  if (!totalMinutes || totalMinutes <= 0) return "0m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const parseAddOns = (serviceDataJson?: string) => {
  if (!serviceDataJson) return [];
  try {
    const parsed = JSON.parse(serviceDataJson);
    return parsed?.selectedAddOns || [];
  } catch {
    return [];
  }
};

const parseServiceLocations = (serviceDataJson?: string) => {
  if (!serviceDataJson) return null;
  try {
    const parsed = JSON.parse(serviceDataJson);
    return {
      pickup: parsed?.pickup || null,
      drop: parsed?.drop || null,
      pricing: parsed?.pricing || null,
    };
  } catch {
    return null;
  }
};

const cleanPayload = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== "") {
      cleaned[key] = value;
    }
  });
  return cleaned as Partial<T>;
};

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const CustomerMyBookingsScreen: React.FC = () => {
  const { showConfirm } = useAlert();

  const [activeTab, setActiveTab] = useState<"bookings" | "packages">("bookings");

  const [triggerGetMyBookings, { data: myBookingsData, isLoading: isLoadingBookings }] =
    useLazyGetMyBookingsQuery();
  const [triggerGetDetails, { data: bookingDetail, isLoading: isLoadingDetail }] =
    useLazyGetBookingDetailsByIdQuery();
  const [triggerGetMyPackages, { data: myPackagesData, isLoading: isLoadingPackages }] =
    useLazyGetMyPackagesQuery();

  const [rescheduleSlots, { isLoading: isLoadingSlots }] = useRescheduleSlotsMutation();
  const [freePackageRescheduleSlots, { isLoading: isLoadingFreePackageSlots }] =
    useFreePackageRescheduleSlotsMutation();
  const [rescheduleBooking, { isLoading: isRescheduling }] = useRescheduleBookingMutation();
  const [cancelBooking, { isLoading: isCancelling }] = useCancelBookingsMutation();
  const [freePackageBooking, { isLoading: isBookingFreePackage }] = useFreePackageBookingMutation();

  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());
  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

  const [rescheduleBookingTarget, setRescheduleBookingTarget] = useState<IMyBookingsResponse | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<BookingSlot[]>([]);
  const [selectedSlotTime, setSelectedSlotTime] = useState<string>("");

  const [cancelBookingTarget, setCancelBookingTarget] = useState<IMyBookingsResponse | null>(null);
  const [cancelReason, setCancelReason] = useState<string>("");

  const [selectedPackageForBooking, setSelectedPackageForBooking] = useState<IMultiPackageUsedresponse | null>(null);
  const [packageBookingDate, setPackageBookingDate] = useState<string>("");
  const [packageAvailableSlots, setPackageAvailableSlots] = useState<BookingSlot[]>([]);
  const [selectedPackageSlotTime, setSelectedPackageSlotTime] = useState<string>("");

  useEffect(() => {
    if (activeTab === "bookings") {
      fetchBookings();
    } else {
      triggerGetMyPackages();
    }
  }, [currentCalendarDate, selectedStatusFilter, activeTab]);

  const fetchBookings = () => {
    const monthRange = getMonthRangeISO(currentCalendarDate);

    const rawPayload = {
      status: selectedStatusFilter.trim() || undefined,
      fromDate: monthRange.fromDate,
      toDate: monthRange.toDate,
      search: searchTerm.trim() || undefined,
    };

    const cleanedPayload = cleanPayload(rawPayload);
    triggerGetMyBookings(cleanedPayload as IGetMyBookingsRequest);
  };

  const bookingsList: IMyBookingsResponse[] = Array.isArray(myBookingsData)
    ? myBookingsData
    : (myBookingsData as any)?.data || [];

  const packagesList: IMultiPackageUsedresponse[] = Array.isArray(myPackagesData)
    ? myPackagesData
    : (myPackagesData as any)?.data || [];

  const filteredBookings = bookingsList.filter((b) => {
    const matchSearch =
      b.serviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.bookingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.petName?.toLowerCase().includes(searchTerm.toLowerCase());

    if (selectedDayDate) {
      const dayISO = formatLocalDateISO(selectedDayDate);
      return matchSearch && b.bookingDate === dayISO;
    }

    return matchSearch;
  });

  const filteredPackages = packagesList.filter((p) => {
    return (
      p.serviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.petName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const year = currentCalendarDate.getFullYear();
  const month = currentCalendarDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const startDay = firstDay.getDay();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPreviousMonth = new Date(year, month, 0).getDate();

  const calendarDays: {
    date: number;
    currentMonth: boolean;
    fullDate: Date;
  }[] = [];

  for (let i = startDay - 1; i >= 0; i--) {
    calendarDays.push({
      date: daysInPreviousMonth - i,
      currentMonth: false,
      fullDate: new Date(year, month - 1, daysInPreviousMonth - i),
    });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      date: i,
      currentMonth: true,
      fullDate: new Date(year, month, i),
    });
  }

  while (calendarDays.length < 42) {
    const nextDate = calendarDays.length - (startDay + daysInMonth) + 1;
    calendarDays.push({
      date: nextDate,
      currentMonth: false,
      fullDate: new Date(year, month + 1, nextDate),
    });
  }

  const handleOpenDetails = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    triggerGetDetails(bookingId);
  };

  const handleDateChangeForReschedule = async (dateStr: string) => {
    setRescheduleDate(dateStr);
    setSelectedSlotTime("");
    if (!rescheduleBookingTarget || !dateStr) return;

    try {
      const slots = await rescheduleSlots({
        bookingId: rescheduleBookingTarget.bookingId,
        bookingDate: dateStr,
      }).unwrap();
      setAvailableSlots(slots || []);
    } catch (err) {
      console.error("Failed to fetch slots:", err);
      setAvailableSlots([]);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!rescheduleBookingTarget || !rescheduleDate || !selectedSlotTime) return;

    try {
      await rescheduleBooking({
        bookingId: rescheduleBookingTarget.bookingId,
        bookingDate: rescheduleDate,
        startTime: selectedSlotTime,
      }).unwrap();

      showConfirm({
        type: "success",
        title: "Rescheduled Successfully!",
        message: `Booking #${rescheduleBookingTarget.bookingNumber} moved to ${rescheduleDate} at ${formatTimeHHMM(selectedSlotTime)}.`,
        confirmText: "OK",
        showCancel: false,
      });

      setRescheduleBookingTarget(null);
      setAvailableSlots([]);
      setSelectedSlotTime("");
      fetchBookings();
    } catch (err) {
      console.error(err);
      showConfirm({
        type: "error",
        title: "Reschedule Failed",
        message: "Unable to reschedule. Please try another slot.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelBookingTarget || !cancelReason.trim()) return;

    try {
      await cancelBooking({
        bookingId: cancelBookingTarget.bookingId,
        cancelReason: cancelReason.trim(),
      }).unwrap();

      showConfirm({
        type: "success",
        title: "Booking Cancelled",
        message: `Booking #${cancelBookingTarget.bookingNumber} has been cancelled.`,
        confirmText: "OK",
        showCancel: false,
      });

      setCancelBookingTarget(null);
      setCancelReason("");
      fetchBookings();
    } catch (err) {
      console.error(err);
      showConfirm({
        type: "error",
        title: "Cancellation Failed",
        message: "Failed to cancel booking. Please try again.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  const handleDateChangeForPackageBooking = async (dateStr: string) => {
    setPackageBookingDate(dateStr);
    setSelectedPackageSlotTime("");

    if (!selectedPackageForBooking || !dateStr) return;

    try {
      const slots = await freePackageRescheduleSlots({
        customerPackageId: selectedPackageForBooking.customerPackageId,
        bookingDate: dateStr,
      }).unwrap();

      setPackageAvailableSlots(slots || []);
    } catch (err) {
      console.error("Failed to fetch slots for package booking:", err);
      setPackageAvailableSlots([]);
    }
  };

  const handleConfirmFreePackageBooking = async () => {
    if (!selectedPackageForBooking || !packageBookingDate || !selectedPackageSlotTime) return;

    const payload: ICreateFreeBookingsRequest = {
      customerPackageId: selectedPackageForBooking.customerPackageId,
      bookingDate: packageBookingDate,
      startTime: selectedPackageSlotTime,
    };

    try {
      await freePackageBooking(payload).unwrap();

      showConfirm({
        type: "success",
        title: "Session Booked!",
        message: `Successfully booked a package session for ${selectedPackageForBooking.serviceName} on ${packageBookingDate} at ${formatTimeHHMM(selectedPackageSlotTime)}.`,
        confirmText: "OK",
        showCancel: false,
      });

      setSelectedPackageForBooking(null);
      setPackageBookingDate("");
      setSelectedPackageSlotTime("");
      setPackageAvailableSlots([]);
      triggerGetMyPackages();
    } catch (err) {
      console.error(err);
      showConfirm({
        type: "error",
        title: "Booking Failed",
        message: "Failed to book package session. Please try a different slot.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "completed":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "needsreschedule":
        return "bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]";
      case "cancelled":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-amber-50 text-amber-700 border-amber-200";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60 py-8 px-4 sm:px-6 lg:px-8 font-nunito text-left antialiased">
      <div className="max-w-7xl mx-auto space-y-6">

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-3 rounded-3xl border border-slate-200/80 shadow-xs">
          <div className="flex items-center gap-2 w-full sm:w-auto bg-slate-100 p-1.5 rounded-2xl">
            <button
              onClick={() => {
                setActiveTab("bookings");
                setSelectedDayDate(null);
              }}
              className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center justify-center gap-2 ${
                activeTab === "bookings"
                  ? "bg-white text-mainBrandColor shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Service Bookings
            </button>

            <button
              onClick={() => {
                setActiveTab("packages");
                setSelectedDayDate(null);
              }}
              className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center justify-center gap-2 ${
                activeTab === "packages"
                  ? "bg-white text-mainBrandColor shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <PackageCheck className="w-4 h-4" />
              My Active Packages
            </button>
          </div>

          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={activeTab === "bookings" ? "Search booking ID, pet or service..." : "Search package or pet..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-2xl text-xs bg-white focus:outline-none focus:border-mainBrandColor"
            />
          </div>
        </div>

        {activeTab === "bookings" && (
          <>
            {!selectedDayDate ? (
              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-mainBrandColor" />
                      {currentCalendarDate.toLocaleString("default", { month: "long", year: "numeric" })}
                    </h2>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() =>
                          setCurrentCalendarDate(
                            new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() - 1, 1)
                          )
                        }
                        className="p-1.5 hover:bg-slate-100 rounded-xl transition cursor-pointer border border-slate-200"
                      >
                        <ChevronLeft className="w-4 h-4 text-slate-600" />
                      </button>

                      <button
                        onClick={() => setCurrentCalendarDate(new Date())}
                        className="text-xs font-bold px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition cursor-pointer"
                      >
                        Today
                      </button>

                      <button
                        onClick={() =>
                          setCurrentCalendarDate(
                            new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 1)
                          )
                        }
                        className="p-1.5 hover:bg-slate-100 rounded-xl transition cursor-pointer border border-slate-200"
                      >
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                    {[
                      { id: "", label: "All Status" },
                      { id: "Confirmed", label: "Confirmed" },
                      { id: "Pending", label: "Pending" },
                      { id: "NeedsReschedule", label: "Needs Reschedule" },
                      { id: "Completed", label: "Completed" },
                      { id: "Cancelled", label: "Cancelled" },
                    ].map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => setSelectedStatusFilter(filter.id)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer whitespace-nowrap ${
                          selectedStatusFilter === filter.id
                            ? "bg-mainBrandColor text-white shadow-sm"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                    {weekDays.map((day) => (
                      <div key={day} className="py-2.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
                    {calendarDays.map((dayObj, index) => {
                      const dateKey = formatLocalDateISO(dayObj.fullDate);
                      const dayBookings = bookingsList.filter((b) => b.bookingDate === dateKey);
                      const isToday = formatLocalDateISO(new Date()) === dateKey;

                      return (
                        <div
                          key={index}
                          onClick={() => setSelectedDayDate(dayObj.fullDate)}
                          className={`min-h-[110px] p-2 transition cursor-pointer flex flex-col justify-between ${
                            !dayObj.currentMonth ? "bg-slate-50/50 text-slate-300" : "bg-white text-slate-700"
                          } hover:bg-slate-50`}
                        >
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                                isToday ? "bg-mainBrandColor text-white shadow-xs" : ""
                              }`}
                            >
                              {dayObj.date}
                            </span>

                            {dayBookings.length > 0 && (
                              <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded-full">
                                {dayBookings.length}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 my-1 overflow-hidden">
                            {dayBookings.slice(0, 2).map((item) => {
                              const statusLower = item.status?.toLowerCase();
                              let badgeClasses = "bg-amber-50 text-amber-800 border-amber-200";
                              if (statusLower === "confirmed") {
                                badgeClasses = "bg-emerald-50 text-emerald-800 border-emerald-200";
                              } else if (statusLower === "needsreschedule") {
                                badgeClasses = "bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]";
                              }

                              return (
                                <div
                                  key={item.bookingId}
                                  className={`text-[10px] p-1 rounded-lg border font-bold truncate ${badgeClasses}`}
                                >
                                  {formatTimeHHMM(item.startTime)} - {item.serviceName}
                                </div>
                              );
                            })}

                            {dayBookings.length > 2 && (
                              <span className="text-[9px] font-extrabold text-mainBrandColor block text-center">
                                +{dayBookings.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-3xl border border-slate-200/80 shadow-xs">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedDayDate(null)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl text-xs font-bold transition cursor-pointer flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Calendar
                    </button>

                    <h3 className="text-base font-extrabold text-slate-800">
                      Appointments on {selectedDayDate.toLocaleDateString("default", { day: "numeric", month: "long", year: "numeric" })}
                      <span className="text-xs font-semibold text-slate-400 ml-2">
                        ({filteredBookings.length} {filteredBookings.length === 1 ? "booking" : "bookings"})
                      </span>
                    </h3>
                  </div>
                </div>

                {isLoadingBookings ? (
                  <div className="py-20 text-center space-y-3 bg-white rounded-3xl border border-slate-100">
                    <RefreshCw className="w-8 h-8 text-mainBrandColor animate-spin mx-auto" />
                    <p className="text-xs font-bold text-slate-400">Loading bookings...</p>
                  </div>
                ) : filteredBookings.length === 0 ? (
                  <div className="bg-white rounded-3xl p-12 text-center border border-dashed border-slate-200 space-y-3 max-w-md mx-auto">
                    <CalendarDays className="w-12 h-12 text-slate-300 mx-auto" />
                    <h3 className="text-base font-bold text-slate-700">No Bookings Found</h3>
                    <p className="text-xs text-slate-400">
                      There are no service bookings scheduled for this date.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBookings.map((booking) => {
                      const statusLower = booking.status?.toLowerCase();
                      const canReschedule =
                        statusLower === "confirmed" ||
                        statusLower === "pending" ||
                        statusLower === "needsreschedule";

                      return (
                        <div
                          key={booking.bookingId}
                          onClick={() => handleOpenDetails(booking.bookingId)}
                          className="bg-white rounded-3xl border border-slate-200/80 p-5 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-4 group relative cursor-pointer"
                        >
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-2.5 py-1 rounded-lg">
                                #{booking.bookingNumber}
                              </span>
                              <span
                                className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${getStatusBadge(
                                  booking.status
                                )}`}
                              >
                                {booking.status}
                              </span>
                            </div>

                            <div>
                              <h3 className="text-base font-extrabold text-slate-800 line-clamp-1 group-hover:text-mainBrandColor transition-colors">
                                {booking.serviceName}
                              </h3>
                              <p className="text-xs font-semibold text-emerald-600 line-clamp-1 mt-0.5">
                                {booking.packageName}
                              </p>
                            </div>

                            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 space-y-2 text-xs font-semibold text-slate-600">
                              <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-[11px]">Pampered Pet:</span>
                                <span className="text-slate-900 font-bold">🐾 {booking.petName}</span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-[11px]">Date:</span>
                                <span className="text-slate-900 font-bold flex items-center gap-1">
                                  <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                  {booking.bookingDate}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-[11px]">Time Window:</span>
                                <span className="text-slate-900 font-bold flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                                  {formatTimeHHMM(booking.startTime)} - {formatTimeHHMM(booking.endTime)} ({formatDurationToHr(booking.durationMinutes)})
                                </span>
                              </div>
                            </div>
                          </div>

                          <div
                            className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 block uppercase">Total Paid</span>
                              <span className="text-base font-black text-slate-900">
                                AED {booking.finalPrice?.toFixed(2)}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5">
                              {canReschedule && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRescheduleBookingTarget(booking);
                                    setRescheduleDate(booking.bookingDate);
                                    handleDateChangeForReschedule(booking.bookingDate);
                                  }}
                                  className={`p-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                                    statusLower === "needsreschedule"
                                      ? "bg-orange-100 hover:bg-orange-200 text-orange-800 animate-pulse"
                                      : "bg-amber-50 hover:bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  Reschedule
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => handleOpenDetails(booking.bookingId)}
                                className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                Details
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "packages" && (
          <div className="space-y-6">
            {isLoadingPackages ? (
              <div className="py-20 text-center space-y-3 bg-white rounded-3xl border border-slate-100">
                <RefreshCw className="w-8 h-8 text-mainBrandColor animate-spin mx-auto" />
                <p className="text-xs font-bold text-slate-400">Loading active packages...</p>
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-dashed border-slate-200 space-y-3 max-w-md mx-auto">
                <PackageCheck className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="text-base font-bold text-slate-700">No Active Packages</h3>
                <p className="text-xs text-slate-400">
                  You don't have any active multi-session packages purchased.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPackages.map((pkg) => {
                  const progressPct = Math.min(
                    100,
                    Math.round((pkg.usedBookings / (pkg.includedBookings || 1)) * 100)
                  );
                  const isFullyUsed = pkg.remainingBookings <= 0;

                  return (
                    <div
                      key={pkg.orderServiceItemId}
                      className="bg-white rounded-3xl border border-slate-200/80 p-5 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-5"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-wider bg-orange-50 text-mainBrandColor px-2.5 py-1 rounded-lg border border-orange-100">
                            Package #{pkg.orderServiceItemId}
                          </span>
                          <span className="text-xs font-bold text-slate-400">
                            Valid {pkg.validForMonths} Mo
                          </span>
                        </div>

                        <div>
                          <h3 className="text-base font-extrabold text-slate-800 line-clamp-1">
                            {pkg.packageName}
                          </h3>
                          <p className="text-xs font-bold text-slate-500 line-clamp-1">
                            {pkg.serviceName}
                          </p>
                        </div>

                        <div className="space-y-1.5 pt-1">
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-slate-500">Sessions Used</span>
                            <span className="text-slate-900">
                              {pkg.usedBookings} / {pkg.includedBookings}
                            </span>
                          </div>
                          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-mainBrandColor transition-all duration-500"
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                        </div>

                        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 space-y-2 text-xs font-semibold text-slate-600">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-[11px]">For Pet:</span>
                            <span className="text-slate-900 font-bold">🐾 {pkg.petName}</span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-[11px]">Remaining:</span>
                            <span className={`font-black ${isFullyUsed ? "text-rose-500" : "text-emerald-600"}`}>
                              {pkg.remainingBookings} Sessions Left
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-[11px]">Expires On:</span>
                            <span className="text-slate-900 font-bold">{pkg.expiresOn}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={isFullyUsed}
                        onClick={() => {
                          setSelectedPackageForBooking(pkg);
                          setPackageBookingDate(formatLocalDateISO(new Date()));
                          handleDateChangeForPackageBooking(formatLocalDateISO(new Date()));
                        }}
                        className={`w-full py-3 rounded-2xl text-xs font-extrabold flex items-center justify-center gap-2 transition cursor-pointer ${
                          isFullyUsed
                            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                            : "bg-mainBrandColor text-white hover:bg-orange-600 shadow-sm"
                        }`}
                      >
                        <PlusCircle className="w-4 h-4" />
                        {isFullyUsed ? "Package Exhausted" : "Book Free Session"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {rescheduleBookingTarget && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-5 border border-slate-100">
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="text-lg font-bold text-slate-900">Reschedule Booking #{rescheduleBookingTarget.bookingNumber}</h3>
                <button
                  onClick={() => {
                    setRescheduleBookingTarget(null);
                    setAvailableSlots([]);
                  }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">
                    Select New Booking Date
                  </label>
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => handleDateChangeForReschedule(e.target.value)}
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold focus:outline-none focus:border-mainBrandColor"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">Available Time Slots</label>

                  {isLoadingSlots || isLoadingFreePackageSlots ? (
                    <div className="py-6 text-center text-xs font-bold text-slate-400">
                      Loading available slots...
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="p-4 bg-slate-50 rounded-2xl text-center text-xs text-slate-400 font-medium">
                      No available slots for this date. Please pick another day.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                      {availableSlots.map((slot, i) => (
                        <button
                          key={i}
                          disabled={!slot.isAvailable}
                          onClick={() => setSelectedSlotTime(slot.startTime)}
                          className={`p-2.5 rounded-xl border text-xs font-bold transition flex flex-col items-center cursor-pointer ${
                            !slot.isAvailable
                              ? "bg-slate-100 text-slate-400 border-slate-200 opacity-50 cursor-not-allowed"
                              : selectedSlotTime === slot.startTime
                              ? "bg-mainBrandColor text-white border-mainBrandColor shadow-sm"
                              : "bg-white text-slate-700 border-slate-200 hover:border-mainBrandColor"
                          }`}
                        >
                          <span>{formatTimeHHMM(slot.startTime)}</span>
                          <span className="text-[9px] font-normal opacity-80">{slot.remainingCapacity} slots left</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setRescheduleBookingTarget(null)}
                  className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={isRescheduling || !selectedSlotTime}
                  onClick={handleConfirmReschedule}
                  className="px-5 py-2.5 bg-mainBrandColor text-white rounded-2xl text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition cursor-pointer"
                >
                  {isRescheduling ? "Updating..." : "Confirm Reschedule"}
                </button>
              </div>
            </div>
          </div>
        )}

        {cancelBookingTarget && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100">
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="text-lg font-bold text-slate-900">Cancel Booking</h3>
                <button
                  onClick={() => setCancelBookingTarget(null)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <p className="text-xs text-slate-500 font-medium">
                  Are you sure you want to cancel booking <span className="font-bold text-slate-800">#{cancelBookingTarget.bookingNumber}</span>?
                </p>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">
                    Reason for Cancellation
                  </label>
                  <textarea
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Tell us why you are cancelling..."
                    className="w-full border border-slate-200 rounded-2xl p-3 text-xs font-medium focus:outline-none focus:border-rose-500 resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setCancelBookingTarget(null)}
                  className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Keep Booking
                </button>

                <button
                  type="button"
                  disabled={isCancelling || !cancelReason.trim()}
                  onClick={handleConfirmCancel}
                  className="px-5 py-2.5 bg-rose-600 text-white rounded-2xl text-xs font-bold hover:bg-rose-700 disabled:opacity-50 transition cursor-pointer"
                >
                  {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedPackageForBooking && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-5 border border-slate-100">
              <div className="flex justify-between items-center border-b pb-3">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Book Package Session</h3>
                  <p className="text-xs font-semibold text-emerald-600 mt-0.5">
                    {selectedPackageForBooking.packageName} ({selectedPackageForBooking.remainingBookings} sessions remaining)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedPackageForBooking(null);
                    setPackageAvailableSlots([]);
                  }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">
                    Select Appointment Date
                  </label>
                  <input
                    type="date"
                    value={packageBookingDate}
                    onChange={(e) => handleDateChangeForPackageBooking(e.target.value)}
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold focus:outline-none focus:border-mainBrandColor"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">Available Time Slots</label>

                  {isLoadingSlots ? (
                    <div className="py-6 text-center text-xs font-bold text-slate-400">
                      Loading slots...
                    </div>
                  ) : packageAvailableSlots.length === 0 ? (
                    <div className="p-4 bg-slate-50 rounded-2xl text-center text-xs text-slate-400 font-medium">
                      No available slots for this date. Please select another day.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                      {packageAvailableSlots.map((slot, i) => (
                        <button
                          key={i}
                          disabled={!slot.isAvailable}
                          onClick={() => setSelectedPackageSlotTime(slot.startTime)}
                          className={`p-2.5 rounded-xl border text-xs font-bold transition flex flex-col items-center cursor-pointer ${
                            !slot.isAvailable
                              ? "bg-slate-100 text-slate-400 border-slate-200 opacity-50 cursor-not-allowed"
                              : selectedPackageSlotTime === slot.startTime
                              ? "bg-mainBrandColor text-white border-mainBrandColor shadow-sm"
                              : "bg-white text-slate-700 border-slate-200 hover:border-mainBrandColor"
                          }`}
                        >
                          <span>{formatTimeHHMM(slot.startTime)}</span>
                          <span className="text-[9px] font-normal opacity-80">{slot.remainingCapacity} slots left</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setSelectedPackageForBooking(null)}
                  className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={isBookingFreePackage || !selectedPackageSlotTime}
                  onClick={handleConfirmFreePackageBooking}
                  className="px-5 py-2.5 bg-mainBrandColor text-white rounded-2xl text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition cursor-pointer"
                >
                  {isBookingFreePackage ? "Booking..." : "Confirm Free Session"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
            selectedBookingId ? "opacity-100 pointer-events-auto" : "opacity-0"
          }`}
        >
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-xs transition-opacity"
            onClick={() => setSelectedBookingId(null)}
          />

          <div
            className={`absolute right-0 top-0 bottom-0 w-full sm:w-[500px] bg-white shadow-2xl transition-transform duration-300 transform flex flex-col ${
              selectedBookingId ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {selectedBookingId && (
              <div className="p-6 flex flex-col h-full space-y-4">
                <div className="flex justify-between items-center border-b pb-4">
                  <h3 className="font-extrabold text-slate-900 text-lg">Booking Detailed Breakdown</h3>
                  <button
                    onClick={() => setSelectedBookingId(null)}
                    className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-2xl transition cursor-pointer"
                  >
                    <X className="w-4 h-4 text-slate-700" />
                  </button>
                </div>

                {isLoadingDetail ? (
                  <div className="py-20 text-center text-xs font-bold text-slate-400">
                    Loading details...
                  </div>
                ) : bookingDetail ? (
                  <div className="flex-1 overflow-y-auto space-y-6 pr-1">
                    <div className="bg-slate-50 p-4 rounded-3xl border border-slate-100 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-400">Booking ID</span>
                        <span className="text-xs font-black text-slate-900">#{bookingDetail.bookingNumber}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-400">Status</span>
                        <span className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${getStatusBadge(bookingDetail.status)}`}>
                          {bookingDetail.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-400">Payment</span>
                        <span className="text-xs font-bold text-emerald-600">{bookingDetail.paymentStatus} ({bookingDetail.paymentMethod || "Online"})</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Service Details</h4>
                      <div className="bg-white border border-slate-200/80 rounded-2xl p-4 space-y-2 text-xs font-semibold text-slate-700">
                        <p className="flex justify-between"><span className="text-slate-400">Service:</span> <span className="font-bold text-slate-900">{bookingDetail.serviceName}</span></p>
                        <p className="flex justify-between"><span className="text-slate-400">Package:</span> <span className="font-bold text-emerald-600">{bookingDetail.packageName}</span></p>
                        <p className="flex justify-between"><span className="text-slate-400">Pet Name:</span> <span className="font-bold text-slate-900">{bookingDetail.petName} ({bookingDetail.petBreed || bookingDetail.petType})</span></p>
                        <p className="flex justify-between"><span className="text-slate-400">Assigned Staff:</span> <span className="font-bold text-slate-900">{bookingDetail.employeeName || "Pending Assign"}</span></p>
                      </div>
                    </div>

                    {(() => {
                      const locations = parseServiceLocations(bookingDetail?.serviceDataJson);
                      if (!locations || (!locations.pickup && !locations.drop)) return null;

                      return (
                        <div className="space-y-2">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Transfer Route & Locations</h4>
                          <div className="space-y-2.5">
                            {locations.pickup && (
                              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 space-y-1.5 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="font-black text-slate-900 flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5 text-mainBrandColor" /> Pickup: {locations.pickup.addressLine2 || "Location"}
                                  </span>
                                </div>
                                <p className="text-slate-600 pl-5">{locations.pickup.address}</p>
                                <div className="pl-5 pt-1">
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${locations.pickup.latitude},${locations.pickup.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-mainBrandColor font-bold inline-flex items-center gap-1 hover:underline"
                                  >
                                    View on Google Maps →
                                  </a>
                                </div>
                              </div>
                            )}

                            {locations.drop && (
                              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 space-y-1.5 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="font-black text-slate-900 flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5 text-rose-500" /> Drop-off: {locations.drop.addressLine2 || "Location"}
                                  </span>
                                </div>
                                <p className="text-slate-600 pl-5">{locations.drop.address}</p>
                                <div className="pl-5 pt-1">
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${locations.drop.latitude},${locations.drop.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-mainBrandColor font-bold inline-flex items-center gap-1 hover:underline"
                                  >
                                    View on Google Maps →
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {parseAddOns(bookingDetail.serviceDataJson).length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Selected Add-Ons</h4>
                        <div className="space-y-1.5">
                          {parseAddOns(bookingDetail.serviceDataJson).map((addon: any, idx: number) => (
                            <div key={idx} className="bg-orange-50/50 border border-orange-100 rounded-xl p-2.5 flex items-center justify-between text-xs font-bold text-slate-800">
                              <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-mainBrandColor" /> {addon.name}</span>
                              <span className="text-mainBrandColor">AED {addon.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {bookingDetail.history && bookingDetail.history.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Activity History</h4>
                        <div className="border-l-2 border-slate-200 pl-4 space-y-3 py-1">
                          {bookingDetail.history.map((hist, i) => (
                            <div key={i} className="text-xs space-y-0.5">
                              <span className="font-bold text-slate-800 block">{hist.action}</span>
                              <span className="text-[10px] text-slate-400 block">{new Date(hist.createdOn).toLocaleString()}</span>
                              {hist.remarks && <p className="text-[11px] text-slate-500 italic">{hist.remarks}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerMyBookingsScreen;